import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { X, Tag } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TaskLabelAssignment, BoardLabel } from "./task-types";

interface TaskLabelSectionProps {
  taskId: string;
  assignedLabels: TaskLabelAssignment[];
  availableLabels: BoardLabel[];
  canEdit: boolean;
  variant?: "default" | "inline";
  hideLabel?: boolean;
}

export function TaskLabelSection({ taskId, assignedLabels, availableLabels, canEdit, variant = "default", hideLabel }: TaskLabelSectionProps) {
  const { toast } = useToast();

  const addLabelMutation = useMutation({
    mutationFn: async (labelId: string) => apiRequest("POST", `/api/tasks/${taskId}/labels`, { labelId }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const removeLabelMutation = useMutation({
    mutationFn: async (assignmentId: string) => apiRequest("DELETE", `/api/tasks/${taskId}/labels/${assignmentId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const unassignedLabels = availableLabels.filter(
    al => !assignedLabels.some(assigned => assigned.labelId === al.id)
  );

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {!hideLabel && <span className="text-xs text-muted-foreground whitespace-nowrap"><Tag className="w-3 h-3 inline mr-0.5" />Labels:</span>}
        {canEdit && unassignedLabels.length > 0 && (
          <Select value="" onValueChange={labelId => addLabelMutation.mutate(labelId)}>
            <SelectTrigger className="h-7 w-auto min-w-[90px] text-xs gap-1" data-testid="task-select-add-label">
              <SelectValue placeholder="Add label" />
            </SelectTrigger>
            <SelectContent>
              {unassignedLabels.map(l => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {assignedLabels.map(l => (
          <Badge key={l.id} variant="outline" className="text-xs" data-testid={`task-badge-label-${l.labelId}`}>
            {l.labelColor && <span className="w-2 h-2 rounded-full mr-1 inline-block" style={{ backgroundColor: l.labelColor }} />}
            {l.labelName}
            {canEdit && (
              <button className="ml-1" onClick={() => removeLabelMutation.mutate(l.id)} data-testid={`task-button-remove-label-${l.labelId}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <Tag className="w-3 h-3" /> Labels
      </Label>
      <div className="flex flex-wrap gap-1">
        {canEdit && unassignedLabels.length > 0 && (
          <Select value="" onValueChange={labelId => addLabelMutation.mutate(labelId)}>
            <SelectTrigger className="w-28 h-7 text-xs" data-testid="task-select-add-label">
              <SelectValue placeholder="Add label" />
            </SelectTrigger>
            <SelectContent>
              {unassignedLabels.map(l => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {assignedLabels.map(l => (
          <Badge key={l.id} variant="outline" className="text-xs" data-testid={`task-badge-label-${l.labelId}`}>
            {l.labelColor && <span className="w-2 h-2 rounded-full mr-1 inline-block" style={{ backgroundColor: l.labelColor }} />}
            {l.labelName}
            {canEdit && (
              <button className="ml-1" onClick={() => removeLabelMutation.mutate(l.id)} data-testid={`task-button-remove-label-${l.labelId}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
}
